import React, { useEffect, useMemo } from "react";

import { Link } from "gatsby";

import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { navigate } from "../../../../../hooks/navigation";
import * as AsyncData from "../../../../../types/AsyncData";
import pushToAnalytics from "../../../../../utils/analytics";
import Headline from "../../../../base/atoms/Headline";
import Button from "../../../../shared/NewForms/Button";
import TermsOfServiceCheckbox from "../../../Thg/components/TermsOfServiceCheckbox";
import useLiquidGasInquiryStore, {
    useLiquidGasInquirySubmitStore,
    useLiquidGasSummaryForm,
} from "./store";

const Row = styled.div`
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    justify-content: space-between;
    gap: 20px;
    padding: 10px 0;
    @media (max-width: 768px) {
        flex-direction: column;
    }
    ${({ theme }) => css`
        border-bottom: 2px dotted ${theme.colors.darkGrey.toString()};
    `}
`;

const DocumentDownload = styled.a`
    text-decoration: underline;
`;
const Section = styled.section`
    & + & {
        margin-top: 40px;
    }
`;
const TermsOfServiceAndButtonWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-top: 40px;
    margin-bottom: 30px;
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: center;
    }

    & button {
        margin-top: 20px;
    }
`;
const LinkWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;
const StyledLink = styled(Link)`
    font-weight: bold;
    text-decoration: underline;
`;
const DataPolicyLink = styled.a`
    text-decoration: underline;
`;
const LiquidGasSummary = () => {
    const { contactForm, questionnaireFormState, pages } =
        useLiquidGasInquiryStore(
            ({ contactForm, questionnaireFormState, pages }) => ({
                contactForm,
                questionnaireFormState,
                pages,
            })
        );
    const { inquiryResponse, submit } = useLiquidGasInquirySubmitStore(
        ({ inquiryResponse, submit }) => ({
            inquiryResponse,
            submit,
        })
    );

    const questions = useMemo(
        () => pages.flatMap((p) => p?.questions ?? []),
        [pages]
    );

    useEffect(() => {
        if (AsyncData.isLoaded(inquiryResponse)) {
            void navigate("/fluessiggas/anfrage/erfolg");
        }
    }, [inquiryResponse]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useLiquidGasSummaryForm();
    const { setCurrentQuestionnaireStep } = useLiquidGasInquiryStore(
        ({ setCurrentQuestionnaireStep }) => ({
            setCurrentQuestionnaireStep,
        })
    );

    const backToQuestionnaire = () => {
        pushToAnalytics("fluessiggas:fragebogen-change_answers", {
            clicked: "change-answers",
        });
        setCurrentQuestionnaireStep(0);
    };

    return (
        <form onSubmit={handleSubmit(submit)}>
            <Section>
                <LinkWrapper>
                    <Headline level={4}>Kontaktdaten</Headline>
                    <StyledLink
                        onClick={() =>
                            pushToAnalytics(
                                "fluessiggas:fragebogen-change_contact",
                                { clicked: "change-contact" }
                            )
                        }
                        to="/fluessiggas/anfrage/"
                    >
                        Ändern
                    </StyledLink>
                </LinkWrapper>
                <Row>
                    <span>
                        {contactForm.formOfAddress} {contactForm.firstname}{" "}
                        {contactForm.lastname}
                    </span>
                </Row>
                {contactForm.companyName && (
                    <Row>
                        <span>{contactForm.companyName}</span>
                    </Row>
                )}
                <Row>
                    <span>{contactForm.email}</span>
                </Row>
                <Row>
                    <span>{contactForm.telephone}</span>
                </Row>
                <Row>
                    <span>
                        {contactForm.street} {contactForm.houseNumber},{" "}
                        {contactForm.zipcode} {contactForm.city}
                    </span>
                </Row>
            </Section>
            <Section>
                <LinkWrapper>
                    <Headline level={4}>Angaben zum Bedarf</Headline>
                    <StyledLink
                        to="/fluessiggas/anfrage/fragebogen"
                        onClick={backToQuestionnaire}
                    >
                        Ändern
                    </StyledLink>
                </LinkWrapper>
                {questions
                    .filter(<a,>(a: a): a is NonNullable<a> => a != null)
                    .map((q, i) => (
                        <Row key={i}>
                            <span>{q.text}</span>
                            <span>
                                {q.type === "options"
                                    ? q.options.find(
                                          (o) =>
                                              o.value ===
                                              questionnaireFormState[q.name]
                                      )?.text
                                    : questionnaireFormState[q.name]}
                            </span>
                        </Row>
                    ))}
            </Section>
            <TermsOfServiceAndButtonWrapper>
                <TermsOfServiceCheckbox
                    name="dataProtectionAccepted"
                    register={register}
                    required={true}
                    error={
                        errors?.dataProtectionAccepted?.type === "required"
                            ? "Bitte akzeptiere die Datenschutzerklärung."
                            : ""
                    }
                >
                    Ich akzeptiere die{" "}
                    <DataPolicyLink
                        href="/datenschutz/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Datenschutzerklärung
                    </DataPolicyLink>
                    .
                </TermsOfServiceCheckbox>
                {AsyncData.isError(inquiryResponse) && (
                    <p>
                        Fehler beim Absenden der Daten. Bitte versuche es später
                        erneut.
                    </p>
                )}
                <Button
                    color="blue"
                    type="submit"
                    loading={AsyncData.isLoading(inquiryResponse)}
                >
                    Abschicken
                </Button>
            </TermsOfServiceAndButtonWrapper>
        </form>
    );
};

export default LiquidGasSummary;
